// THE REBUILT CONSOLE'S SHELL (UI rebuild batch 1 — Astra's navigation; behind lib/ui-v2.ts). ConsoleShell draws
// this when batch 1 is on and its own chrome when it is off.
//
//   laptop (lg+)  a 212px sidebar — Daily work, Control (closing with More), who is signed in — and a top bar with
//                 the screen's group, the ⌘K search and New entry; the page in a column capped at 1304px.
//   phone         a slim bar (DubaiOTC / Books, search), the page, and the tabs: Home · Parties · New · Chain · More. Trades, Money movements, Settle later
//                 and Treasury are tiles on More; every door that records something is on New entry.
//
// Kept from the old shell, deliberately: THE NEWER-BUILD BAR and the reload on the next route change (a tab left open
// across a deploy booked 207,955 USDT through superseded code on 22 Aug 2026); the phone frame (the app is a box the
// height of the visible area and <main> scrolls inside it, so the keyboard shortens the app instead of panning it).
// Every word and icon comes from lib/routes.ts.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { useIsDesktop } from '../../lib/useIsDesktop';
import { useNewerBuild } from '../../lib/useNewerBuild';
import { PHONE_TABS, navTitle, railRoutes, routeAt, screenGroup, screenTitle, sidebarOwner, phoneTabOwner, type RouteEntry } from '../../lib/routes';
import { fmtWeekday } from '../../lib/format';
import { CommandPalette } from '../CommandPalette';
import { PREVIEW_ON } from '../../lib/ui-v2';

const DAILY = railRoutes('daily');
const CONTROL = railRoutes('control');
// ONE CLICK TO THE REPORTS AND THE DESKS (his "restore most the lost things", 1 Nov 2026): the old rail's More fold reached
// them in one; the rebuild had put them two away, on the More page. "Reports" is one item (it opens Profit; the four reports
// are tabs there) and Exchanges sit under Control — two groups still (DESIGN-SYSTEM §6). More keeps the rest.
const REPORTS: RouteEntry = { ...routeAt('/profit'), title: 'Reports', v2: { ...routeAt('/profit').v2, title: undefined } };
const MORE = routeAt('/more');
const TABS: RouteEntry[] = PHONE_TABS.map(routeAt);
// Eight tabs share 360px in the `daily` set, 45 each: "Treasury" at the label role fits only tightened (main's rule).
const TIGHT = TABS.length > 5 ? 'tracking-tight' : '';
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function SideItem({ r, lit }: { r: RouteEntry; lit: boolean }) {
  const Icon = r.icon;
  return (
    <Link to={r.path} aria-current={lit ? 'page' : undefined} className={`nav-item ${lit ? 'nav-item-on' : ''}`}>
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />}
      <span className="truncate">{navTitle(r)}</span>
    </Link>
  );
}

/** Who is signed in, from /api/me — drawn only from a successful read. */
function SignedIn() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  if (readState(me) !== 'known' || !me.data) return null;
  return (
    <div className="border-t border-white/10 px-3 pt-4 text-meta">
      <div className="truncate font-medium text-white">{me.data.displayName}</div>
      <div className="text-nav-text">{me.data.role === 'read_only' ? 'Read only' : 'Full access'}</div>
      {/* The owner's preview of the rebuilt console (lib/ui-v2.ts): the way back, one quiet link. */}
      {PREVIEW_ON && <Link to="/ui-preview" className="mt-2 inline-block text-nav-text underline-offset-2 hover:text-white hover:underline">Today&apos;s console</Link>}
    </div>
  );
}

function initials(name: string | undefined): string {
  if (!name) return '';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

function TopBar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { pathname } = useLocation();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const who = readState(me) === 'known' ? initials(me.data?.displayName) : '';
  return (
    <header className="sticky top-0 z-20 hidden h-16 items-center justify-between gap-4 border-b border-stroke bg-panel px-8 lg:ml-[212px] lg:flex">
      {/* Where he is and what day it is in Dubai (the old top bar's title and date): quiet text, the screen's name the one
          word in the text colour. */}
      <div className="text-meta text-tg-hint">
        {/* A screen that names its own group (New entry) says it once, not "New entry / New entry". */}
        {screenGroup(pathname) !== screenTitle(pathname) && <>{screenGroup(pathname)} <span className="mx-2">/</span> </>}
        <span className="font-medium text-tg-text">{screenTitle(pathname)}</span>
        <span className="ml-3">{fmtWeekday().replace(/ \d{4}$/, '')}</span>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={onOpenPalette}
          className="flex min-h-[40px] min-w-[240px] items-center gap-2 rounded-lg border border-stroke bg-panel-2 px-3 text-meta text-tg-hint transition-colors hover:border-tg-accent">
          <Search className="h-4 w-4" aria-hidden />
          <span>Search the Books</span>
          <kbd className="ml-auto rounded border border-stroke px-1 font-mono text-label">{IS_MAC ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <Link to="/new" className="btn-secondary px-3 text-body"><Plus className="h-4 w-4" aria-hidden />New entry</Link>
        {who && <span className="grid h-8 w-8 place-items-center rounded-full bg-tg-accent/15 text-label font-semibold text-tg-accent" aria-hidden>{who}</span>}
      </div>
    </header>
  );
}

export function ShellV2({ children }: { children: ReactNode }) {
  const isDesktop = useIsDesktop();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { pathname, search } = useLocation();
  const lit = sidebarOwner(pathname);
  const tab = phoneTabOwner(pathname);

  // THE NEWER-BUILD BAR (lib/useNewerBuild.ts), as the old shell does it: the bar, and on the NEXT route change while it
  // is up the tab reloads onto the new path. The path decides, never the query (a ?party= rewrite mid-form must not
  // reload and eat his typing).
  const newer = useNewerBuild();
  const seenAt = useRef<string | null>(null);
  useEffect(() => {
    if (!newer) return;
    if (seenAt.current === null) { seenAt.current = pathname; return; }
    if (pathname !== seenAt.current) window.location.assign(`${pathname}${search}`);
  }, [newer, pathname]);   // eslint-disable-line react-hooks/exhaustive-deps

  // BACK ONLINE, READ AGAIN (the energy lane's finding, 27 Oct 2026; lib/read-state.ts offlineFailure). The read client starts
  // every page "online", so a page opened with no connection never hears it went offline — and then never hears it came back:
  // its own refetch-on-reconnect does not fire. The screens say such a read is waiting; this is what ends the wait. The reads on
  // show that failed or are stale are asked again. Reads only: no mutation is resumed or re-sent from here.
  const qc = useQueryClient();
  useEffect(() => {
    const back = () => void qc.refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' || q.isStale() });
    window.addEventListener('online', back);
    return () => window.removeEventListener('online', back);
  }, [qc]);

  return (
    <div className="flex min-h-screen flex-col max-lg:h-[var(--app-height,100dvh)] max-lg:min-h-0 max-lg:overflow-hidden">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[212px] flex-col gap-6 bg-nav px-3 pb-4 pt-6 text-nav-text lg:flex">
        <Link to="/" className="flex items-center gap-3 px-3 text-white">
          <span className="grid h-9 w-9 place-items-center rounded-lg border border-tg-accent/60 text-title font-medium text-tg-accent" aria-hidden>D</span>
          <span>
            <strong className="block text-title font-semibold leading-tight">Books</strong>
            <span className="text-label tracking-widest text-nav-text">DUBAIOTC</span>
          </span>
        </Link>
        <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain">
          <div className="flex flex-col gap-1">
            <div className="px-3 pb-2 text-label uppercase tracking-wider text-nav-text/80">Daily work</div>
            {DAILY.map((r) => <SideItem key={r.path} r={r} lit={lit === r.path} />)}
          </div>
          <div className="flex flex-col gap-1">
            <div className="px-3 pb-2 text-label uppercase tracking-wider text-nav-text/80">Control</div>
            <SideItem r={REPORTS} lit={lit === REPORTS.path} />
            {CONTROL.map((r) => <SideItem key={r.path} r={r} lit={lit === r.path} />)}
            <SideItem r={MORE} lit={lit === '/more'} />
          </div>
        </nav>
        <SignedIn />
      </aside>

      {isDesktop && <TopBar onOpenPalette={() => setPaletteOpen(true)} />}
      {/* THE PHONE'S BAR (Astra's): where he is, and the search — the same ⌘K palette, which fits a phone (92vw). */}
      {!isDesktop && (
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-stroke bg-panel pl-4 pr-1">
          <Link to="/" className="tap-44 text-body font-semibold">DubaiOTC <span className="font-normal text-tg-hint">/ Books</span></Link>
          <button type="button" onClick={() => setPaletteOpen(true)} aria-label="Search the Books" className="grid h-11 w-11 place-items-center rounded-lg text-tg-hint hover:bg-panel-2">
            <Search className="h-5 w-5" aria-hidden />
          </button>
        </header>
      )}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      <main className="min-h-0 flex-1 p-4 pb-24 max-lg:overflow-y-auto max-lg:overscroll-contain lg:ml-[212px] lg:px-8 lg:pb-16 lg:pt-6">
        <div className="lg:mx-auto lg:max-w-[1304px]">
          {newer && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-tg-accent/40 bg-tg-accent/10 px-3 py-2 text-meta text-tg-text">
              <span>A newer console is ready</span>
              <button type="button" className="btn-link text-meta font-medium" onClick={() => window.location.reload()}>Reload</button>
            </div>
          )}
          {children}
        </div>
      </main>

      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 flex border-t border-stroke bg-panel px-1 pt-2 pb-[max(8px,env(safe-area-inset-bottom))] lg:hidden">
        {TABS.map((r) => {
          const on = tab === r.path;
          const Icon = r.icon;
          return (
            <NavLink key={r.path} to={r.path} end={r.end ?? false} aria-current={on ? 'page' : undefined}
              className={`flex min-h-[48px] min-w-0 flex-1 flex-col items-center justify-center gap-1 text-label ${TIGHT} ${on ? 'text-tg-accent' : 'text-tg-hint'}`}>
              {Icon && (
                <span className={`rounded-md p-0.5 ${r.path === '/new' ? 'bg-tg-accent/15 text-tg-accent' : ''}`}>
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
              )}
              <span className="max-w-full truncate">{r.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
